import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel } from "react-icons/md";
import * as yup from "yup";
import { ObjectShape } from "yup/lib/object";

import useYupValidation from "../../../../../hooks/useYupValidation";
import ConfirmationModal from "../../../../../components/Dialogs/ConfirmationModal";
import { Input } from "../../../../../components/Form";
import { Color } from "../../../../../components/Form/Button";
import useStyles from "./styles";

export interface CancelQuoteConfirmationModalHandles {
  open: (id: number, reference?: string) => void;
  close: () => void;
}
interface CancelQuoteConfirmationModalProps {
  closeModal?: () => void;
  onSubmit: (id: number, reason?: string) => void;
  showReason?: boolean;
  loadingAction?: boolean;
}

const CancelQuoteConfirmationModal: ForwardRefRenderFunction<
  CancelQuoteConfirmationModalHandles,
  CancelQuoteConfirmationModalProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { closeModal, showReason = false, loadingAction, onSubmit } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [idValue, setIdValue] = useState<number>(0);
  const [reason, setReason] = useState<string>("");

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [reference, setReference] = useState<string>("");

  const open = useCallback((id: number, newReference?: string) => {
    if (id) setIdValue(id);
    if (newReference) setReference(newReference);
    setOpenModal(true);
  }, []);
  const close = useCallback(() => {
    setOpenModal(false);
    setIdValue(0);
    setReason("");
    setErrors([]);
    setReference("");
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleValidateCancelReason = (): boolean => {
    try {
      const additions = {
        reason: yup.string().required(translate("thisFieldIsRequired")),
      } as ObjectShape;

      const schema = yup.object().shape(additions);

      schema.validateSync(
        {
          reason,
        },
        { abortEarly: false }
      );

      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
    if (errors.length !== 0) return false;

    return true;
  };

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useEffect(() => {
    if (isSubmitted) {
      handleValidateCancelReason();
    }
  }, [reason]);

  const [isSubmitted, setIsSubmitted] = useState<boolean>();
  const onValidateCancel = () => {
    setIsSubmitted(true);
    if (handleValidateCancelReason()) onSubmitCancel();
  };

  const onSubmitCancel = () => {
    onSubmit(idValue, reason);
  };

  return (
    <ConfirmationModal
      width="520px"
      open={openModal}
      closeModal={() => {
        if (closeModal) closeModal();
        setReason("");
        close();
      }}
      type="error"
      title={translate("cancelQuote")}
      leftIconTitle={<MdOutlineCancel color="#F04438" size={20} />}
      loading={loadingAction}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      textCancel={translate("back")}
      textSubmit={translate("yesCancelQuote")}
      submit={onValidateCancel}
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item className={classes.cancelModalContainer}>
          <div data-cy="confirmationModalCancelBody" className={classes.descriptionModalConfirm}>
            <span>
              {translate("areYouSureYouWantToCancelTheQuote")} {`${reference}`}?
            </span>
            {showReason && (
              <Input
                label={translate("reason")}
                placeholder={translate("reason")}
                required
                multiline
                fullWidth
                minRows={2}
                maxRows={3}
                maxLength={255}
                value={reason}
                error={fieldHasError("reason")}
                errorMessage={getErrorByField("reason")}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
            <span className="error">{translate("thisActionIsPermanentAndCannotBeUndone")}</span>
          </div>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(CancelQuoteConfirmationModal);
